import { sanitizeRichTextHtml } from './utils/sanitizeHtml';
//MetadataTranslationTabContent.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Input, Tooltip, Tag, message, Button, Modal, Space, Spin, theme } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, CopyOutlined, ExportOutlined, QuestionCircleOutlined, SearchOutlined, EyeOutlined, BulbOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import axios from 'axios';
import 'react-quill/dist/quill.snow.css';
import TermScanPanel from './components/TermScanPanel';
import TermScanLaunchModal from './components/TermScanLaunchModal';
import FeatherSpinner from './components/FeatherSpinner';
import BlurbWordCounter from './components/BlurbWordCounter';
import MetadataSection from './metadata/MetadataSection';
import { CreditBalance, TranslationProgressMap, TranslationStatus } from './types';
import {
  isMissingTranslatedMetadataValue,
  normalizeTranslatedMetadataValue,
  NotTranslatedPlaceholder,
} from './utils/localizedMetadataDisplay';
import { resolveAccountTerms, resolveSeriesTerms } from './utils/glossaryKeys';
import { splitLanguagePair } from './utils/languagePairs';
import { isActiveGlossaryScan } from './utils/glossaryScan';
import { isTutorialSandboxModeEnabled } from './utils/tutorialMode';
import { getAuthHeaders } from './utils/authHeaders';
import { useTranslationStatus } from './TranslationStatusContext';
import { useTutorialSandbox } from './tutorials/TutorialSandboxProvider';
import type { CreditTopUpContext } from './types/creditTopUp';
import type { LocalizedMetadataContentMode } from './metadata/LocalizedMetadataWorkspace';
import {
  formatBlurbWordLimitMessage,
  getBlurbWordCount,
  MAX_BLURB_TRANSLATION_WORDS,
} from './utils/metadataFreeRuns';

const TERM_SCAN_JOB_TYPES = new Set(['glossary scan', 'ai glossary scan', 'term scan', 'terms scan']);
const TERM_SCAN_TERMINAL_STATES = new Set([
  'completed',
  'succeeded',
  'finished',
  'failed',
  'error',
  'expired',
  'interrupted',
  'released',
  'cancelled',
  'canceled',
  'aborted',
]);
const TERM_SCAN_SUCCESS_STATES = new Set(['completed', 'succeeded', 'finished']);
const TERM_SCAN_RUNNING_STATES = new Set(['pending', 'queued', 'running', 'retrying']);
const normalizeLangKey = (value?: string | null): string => (value || '').toString().trim().toLowerCase();
const hasTranslatedMetadataValue = (value?: string | null): boolean => !isMissingTranslatedMetadataValue(value);
type MetadataActionButtonSize = 'small' | 'middle' | 'large';
const AI_UTILITY_BUTTON_CLASS = 'localized-ai-utility-button';

const resolveTermScanJobState = (entry?: TranslationStatus | null): string => {
  const status = (entry?.status || '').toString().trim().toLowerCase();
  const state = (entry?.processingState || '').toString().trim().toLowerCase();
  if (state && TERM_SCAN_TERMINAL_STATES.has(status) && !TERM_SCAN_TERMINAL_STATES.has(state)) {
    return status;
  }
  return state || status;
};

interface MetadataTranslationTabContentProps {
  pair: string;
  titleTranslations: { [key: string]: string };
  seriesTranslations: { [key: string]: string };
  seriesDescriptionTranslations?: { [key: string]: string };
  blurbTranslations: { [key: string]: string };
  keywordTranslations: { [key: string]: string[] };
  neverTranslateWords: { [key: string]: string[] };
  alwaysTranslateWords: { [key: string]: string[] };
  accountNeverTranslateWords?: { [key: string]: string[] };
  accountAlwaysTranslateWords?: { [key: string]: string[] };
  seriesNeverTranslateWords?: { [key: string]: string[] };
  seriesAlwaysTranslateWords?: { [key: string]: string[] };
  setAccountNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAccountAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setSeriesTranslateRules?: React.Dispatch<
    React.SetStateAction<{
      alwaysTranslate: Record<string, string[]>;
      neverTranslate: Record<string, string[]>;
    }>
  >;
  bookId: string;
  auth: any;
  handleDeleteTranslatedChapters: (sourceLang: string, targetLang: string) => void;
  handleDeleteProofreadChapters: (sourceLang: string, targetLang: string) => void;
  setIsNeverTranslateModalVisible: (visible: boolean) => void;
  setIsAlwaysTranslateModalVisible: (visible: boolean) => void;
  setCurrentLanguagePair: (pair: string) => void;
  setTitleTranslations: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  setSeriesTranslations: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  setBlurbTranslations: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  exportBlurbToTxt: () => void;
  apiBase: string;
  resolvedUserId?: string;
  selectedSeriesId?: string | null;
  setNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  creditBalance?: CreditBalance | null;
  fetchCreditBalance?: (userId: string) => Promise<void> | void;
  glossaryScanRequest?: { pair: string; nonce: number } | null;
  onGlossaryScanRequestHandled?: () => void;
  glossaryScanReviewRequest?: { pair: string; nonce: number } | null;
  onGlossaryScanReviewRequestHandled?: () => void;
  translationProgress?: TranslationProgressMap;
  onTopUpCredits?: (context: CreditTopUpContext) => void;
  onOpenKeywordSuggestions?: () => void;
  contentMode?: LocalizedMetadataContentMode;
  metadataFieldsMode?: 'hidden' | 'manual' | 'existing' | 'all';
  onManualMetadataSaved?: () => void;
  translationGuidanceLead?: React.ReactNode;
  storefrontCoverSection?: React.ReactNode;
  includeSeriesMetadata?: boolean;
  onOpenSeriesManager?: () => void;
}

const MetadataTranslationTabContent: React.FC<MetadataTranslationTabContentProps> = ({
  pair,
  titleTranslations,
  seriesTranslations,
  seriesDescriptionTranslations = {},
  blurbTranslations,
  keywordTranslations,
  neverTranslateWords,
  alwaysTranslateWords,
  accountNeverTranslateWords = {},
  accountAlwaysTranslateWords = {},
  seriesNeverTranslateWords = {},
  seriesAlwaysTranslateWords = {},
  bookId,
  auth,
  handleDeleteTranslatedChapters,
  handleDeleteProofreadChapters,
  setIsNeverTranslateModalVisible,
  setIsAlwaysTranslateModalVisible,
  setCurrentLanguagePair,
  setTitleTranslations,
  setSeriesTranslations,
  setBlurbTranslations,
  exportBlurbToTxt,
  apiBase,
  resolvedUserId,
  selectedSeriesId,
  setNeverTranslateWords,
  setAlwaysTranslateWords,
  setAccountNeverTranslateWords,
  setAccountAlwaysTranslateWords,
  setSeriesTranslateRules,
  creditBalance,
  fetchCreditBalance,
  glossaryScanRequest,
  onGlossaryScanRequestHandled,
  glossaryScanReviewRequest,
  onGlossaryScanReviewRequestHandled,
  translationProgress,
  onTopUpCredits,
  onOpenKeywordSuggestions,
  contentMode,
  metadataFieldsMode = 'all',
  onManualMetadataSaved,
  translationGuidanceLead,
  storefrontCoverSection,
  includeSeriesMetadata = true,
  onOpenSeriesManager,
}) => {
  const { token } = theme.useToken();
  const tutorialSandbox = useTutorialSandbox();
  const {
    translations: activeJobs,
    refreshTranslationsImmediate,
    addClientIntent,
  } = useTranslationStatus();
  const { sourceLang, targetLang } = splitLanguagePair(pair);
  const resolvedContentMode = contentMode || metadataFieldsMode;
  const shouldManageTermScan = !contentMode || ['all', 'hidden', 'manual', 'existing', 'rules'].includes(resolvedContentMode);
  const canScanGlossary = Boolean(bookId && sourceLang && targetLang && pair !== 'original');
  const normalizedTargetLang = (targetLang || '').trim();
  const translationProgressEntry = normalizedTargetLang ? translationProgress?.[normalizedTargetLang] : undefined;
  const translationProgressPercent = translationProgressEntry
    ? translationProgressEntry.percentage
    : null;
  const isTutorialSandboxMode = useMemo(() => isTutorialSandboxModeEnabled(), []);
  const tutorialFixturePayload = tutorialSandbox.enabled && tutorialSandbox.mode === 'fixture' && tutorialSandbox.fixturePayload
    ? tutorialSandbox.fixturePayload
    : null;
  const titleTranslation = normalizeTranslatedMetadataValue(titleTranslations[pair]);
  const seriesTranslation = normalizeTranslatedMetadataValue(seriesTranslations[pair]);
  const seriesDescriptionTranslation = normalizeTranslatedMetadataValue(seriesDescriptionTranslations[pair]);
  const blurbTranslation = normalizeTranslatedMetadataValue(blurbTranslations[pair]);
  const keywordsTranslation = keywordTranslations[pair] || [];
  const seriesNeverTerms = resolveSeriesTerms(seriesNeverTranslateWords, targetLang, pair);
  const seriesAlwaysPairs = resolveSeriesTerms(seriesAlwaysTranslateWords, targetLang, pair);
  const accountNeverTerms = resolveAccountTerms(accountNeverTranslateWords, targetLang);
  const accountAlwaysPairs = resolveAccountTerms(accountAlwaysTranslateWords, targetLang);
  const bookNeverTerms = neverTranslateWords[pair] || [];
  const bookAlwaysPairs = alwaysTranslateWords[pair] || [];


  const [editingTranMetadataTitle, setEditingTranMetadataTitle] = useState(false);
  const [editingTranMetadataSeries, setEditingTranMetadataSeries] = useState(false);
  const [editingTranMetadataBlurb, setEditingTranMetadataBlurb] = useState(false);
  const [tempTranMetadataTitle, setTempTranMetadataTitle] = useState('');
  const [tempTranMetadataSeries, setTempTranMetadataSeries] = useState('');
  const [tempTranMetadataBlurb, setTempTranMetadataBlurb] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTermScanLaunchOpen, setIsTermScanLaunchOpen] = useState(false);
  const [isTermScanReviewOpen, setIsTermScanReviewOpen] = useState(false);
  const [isTermScanReviewExpanded, setIsTermScanReviewExpanded] = useState(false);
  const [termScanRun, setTermScanRun] = useState<any | null>(null);
  const [termScanLatestLoading, setTermScanLatestLoading] = useState(false);
  const [termScanLatestError, setTermScanLatestError] = useState<string | null>(null);
  const [termScanApplying, setTermScanApplying] = useState(false);
  const [termScanTourReady, setTermScanTourReady] = useState(false);
  const termScanTourTriggerRef = useRef<(() => void) | null>(null);
  const termScanFetchIdRef = useRef(0);
  const lastTermScanStateRef = useRef<string | null>(null);
  const lastPairRef = useRef<string | null>(null);

  const handleTermScanTourControlChange = useCallback((openTour: (() => void) | null) => {
    termScanTourTriggerRef.current = openTour;
    setTermScanTourReady(Boolean(openTour));
  }, []);

  useEffect(() => {
    if (metadataFieldsMode !== 'manual') return;
    setTempTranMetadataTitle(titleTranslation);
    setTempTranMetadataSeries(seriesTranslation);
    setTempTranMetadataBlurb(blurbTranslation);
  }, [metadataFieldsMode, pair, titleTranslation, seriesTranslation, blurbTranslation]);

  useEffect(() => {
    if (lastPairRef.current === pair) return;
    lastPairRef.current = pair;
    setTermScanRun(null);
    setTermScanLatestError(null);
    setTermScanLatestLoading(false);
  }, [pair]);
  const availableCredits = (() => {
    const raw = creditBalance?.available_balance ?? creditBalance?.credit_balance;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  })();
  const termScanReviewModalTitle =
    sourceLang && targetLang && pair !== 'original'
      ? `Glossary Scan Results (${sourceLang} \u2192 ${targetLang})`
      : 'Glossary Scan Results';
  const termScanReviewModalWidth = isTermScanReviewExpanded ? 'min(1320px, calc(100vw - 24px))' : 1080;
  const termScanReviewModalBodyStyle: React.CSSProperties = {
    height: isTermScanReviewExpanded ? 'calc(100vh - 72px)' : 'calc(100vh - 116px)',
    maxHeight: isTermScanReviewExpanded ? 'calc(100vh - 72px)' : 'calc(100vh - 116px)',
    overflow: 'hidden',
    padding: isTermScanReviewExpanded ? '12px 16px 16px' : '14px 20px 18px',
  };
  const termScanReviewModalContentStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    minHeight: 0,
    height: '100%',
  };
  const renderTermScanReviewModalTitle = () => (
    <div className="translation-modal-titlebar">
      <span className="translation-modal-titlebar__title">{termScanReviewModalTitle}</span>
      <div className="translation-modal-titlebar__actions">
        <Tooltip title={isTermScanReviewExpanded ? 'Restore review window' : 'Expand review window'}>
          <Button
            type="text"
            size="small"
            icon={isTermScanReviewExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={() => setIsTermScanReviewExpanded((expanded) => !expanded)}
            aria-label={isTermScanReviewExpanded ? 'Restore review window' : 'Expand review window'}
            className="translation-modal-titlebar__icon-button"
          />
        </Tooltip>
        <Button
          type="text"
          size="small"
          icon={<QuestionCircleOutlined />}
          onClick={() => termScanTourTriggerRef.current?.()}
          disabled={!termScanTourReady}
          className="translation-modal-titlebar__tour app-tour-trigger"
        >
          Take a tour
        </Button>
      </div>
    </div>
  );
  const termScanJob = useMemo(() => {
    if (!bookId || !canScanGlossary) return null;
    const sourceKey = normalizeLangKey(sourceLang);
    const targetKey = normalizeLangKey(targetLang);
    const matches = (activeJobs || []).filter((job) => {
      if (!job || job.bookId !== bookId) return false;
      const typeKey = (job.type || '').toString().trim().toLowerCase();
      if (!TERM_SCAN_JOB_TYPES.has(typeKey)) return false;
      if (sourceKey && normalizeLangKey(job.sourceLang) !== sourceKey) return false;
      if (targetKey && normalizeLangKey(job.targetLang) !== targetKey) return false;
      return true;
    });
    if (matches.length === 0) return null;
    return matches.sort((a, b) => {
      const aTime = new Date(a.timestamp || 0).getTime();
      const bTime = new Date(b.timestamp || 0).getTime();
      return bTime - aTime;
    })[0];
  }, [activeJobs, bookId, canScanGlossary, sourceLang, targetLang]);
  const termScanJobState = useMemo(() => resolveTermScanJobState(termScanJob), [termScanJob]);
  const termScanJobInFlight = termScanJobState ? TERM_SCAN_RUNNING_STATES.has(termScanJobState) : false;
  const termScanJobCompleted = termScanJobState ? TERM_SCAN_SUCCESS_STATES.has(termScanJobState) : false;
  const termScanRunState = (termScanRun?.status || '').toString().trim().toLowerCase();
  const termScanRunInFlight = isActiveGlossaryScan(termScanRunState);
  const termScanRunCompleted = ['completed', 'succeeded', 'finished'].includes(termScanRunState);
  const termScanInFlight = canScanGlossary && (termScanJobInFlight || termScanRunInFlight);
  const termScanCompleted = canScanGlossary && (termScanJobCompleted || termScanRunCompleted);
  const termScanReviewed = Boolean(
    termScanRun?.reviewedAt || termScanRun?.reviewed_at || termScanRun?.reviewedBy,
  );
  const hasGlossaryScanToReview = Boolean(termScanRun?.runId);
  const showGlossaryScanReadyIndicator = Boolean(
    termScanCompleted && termScanRun?.runId && !termScanReviewed,
  );
  const canReviewGlossaryScan = Boolean(canScanGlossary && termScanRun?.runId);
  const reviewGlossaryScanDisabled = !canReviewGlossaryScan || termScanInFlight;
  const hideGlossaryScanAction = Boolean(
    pair !== 'original' &&
    typeof translationProgressPercent === 'number' &&
    translationProgressPercent >= 75 &&
    hasGlossaryScanToReview,
  );
  const handleOpenGlossaryScan = useCallback(() => {
    const userId = resolvedUserId || auth?.currentUser?.uid || '';
    if (userId && fetchCreditBalance) {
      try {
        void fetchCreditBalance(userId);
      } catch {
        // ignore credit refresh failures here
      }
    }
    setIsTermScanLaunchOpen(true);
  }, [auth?.currentUser?.uid, fetchCreditBalance, resolvedUserId]);

  useEffect(() => {
    if (!shouldManageTermScan) {
      return;
    }
    if (!glossaryScanRequest || glossaryScanRequest.pair !== pair) {
      return;
    }
    if (!sourceLang || !targetLang || pair === 'original') {
      onGlossaryScanRequestHandled?.();
      return;
    }
    handleOpenGlossaryScan();
    onGlossaryScanRequestHandled?.();
  }, [
    glossaryScanRequest?.nonce,
    glossaryScanRequest?.pair,
    handleOpenGlossaryScan,
    onGlossaryScanRequestHandled,
    pair,
    shouldManageTermScan,
    sourceLang,
    targetLang,
  ]);

  useEffect(() => {
    if (!shouldManageTermScan) {
      return;
    }
    if (!glossaryScanReviewRequest || glossaryScanReviewRequest.pair !== pair) {
      return;
    }
    if (!sourceLang || !targetLang || pair === 'original') {
      return;
    }
    setIsTermScanReviewOpen(true);
    onGlossaryScanReviewRequestHandled?.();
  }, [glossaryScanReviewRequest?.nonce, glossaryScanReviewRequest?.pair, onGlossaryScanReviewRequestHandled, pair, shouldManageTermScan, sourceLang, targetLang]);

  const fetchLatestTermScan = useCallback(async () => {
    const userId = resolvedUserId || auth?.currentUser?.uid || '';
    if (!shouldManageTermScan || !bookId || !userId || !sourceLang || !targetLang || pair === 'original') {
      return;
    }
    if (isTutorialSandboxMode && bookId === 'tutorial-book') {
      setTermScanRun(tutorialFixturePayload?.termScanRun || null);
      setTermScanLatestLoading(false);
      setTermScanLatestError(null);
      return;
    }
    const fetchId = ++termScanFetchIdRef.current;
    setTermScanLatestLoading(true);
    setTermScanLatestError(null);
    try {
      const resp = await axios.post(`${apiBase}/books/${bookId}/term-scan/latest-matrix`, {
        userId,
        sourceLang,
        targetLangs: [targetLang],
      });
      if (fetchId !== termScanFetchIdRef.current) return;
      const matrix = resp?.data?.matrix || {};
      const matrixEntry =
        matrix?.[targetLang] ||
        (Object.values(matrix || {})[0] as any) ||
        null;
      const run = matrixEntry?.run || null;
      setTermScanRun(run);
    } catch (err: any) {
      if (fetchId !== termScanFetchIdRef.current) return;
      // eslint-disable-next-line no-console
      console.error('Failed to load latest term scan run', err);
      setTermScanLatestError(err?.response?.data?.detail || err?.message || 'Unable to load latest scan.');
    } finally {
      if (fetchId === termScanFetchIdRef.current) {
        setTermScanLatestLoading(false);
      }
    }
  }, [apiBase, auth?.currentUser?.uid, bookId, isTutorialSandboxMode, pair, resolvedUserId, shouldManageTermScan, sourceLang, targetLang, tutorialFixturePayload]);

  useEffect(() => {
    const userId = resolvedUserId || auth?.currentUser?.uid || '';
    if (!shouldManageTermScan || !bookId || !userId || !sourceLang || !targetLang || pair === 'original') {
      setTermScanRun(null);
      setTermScanLatestLoading(false);
      setTermScanLatestError(null);
      return;
    }
    void fetchLatestTermScan();
  }, [auth?.currentUser?.uid, bookId, fetchLatestTermScan, pair, resolvedUserId, shouldManageTermScan, sourceLang, targetLang]);

  useEffect(() => {
    const previous = lastTermScanStateRef.current;
    lastTermScanStateRef.current = termScanJobState || null;
    // Completed scans clear their lock, so the shared feed can remove the job
    // without ever exposing a terminal state. Read the canonical run on removal
    // and on shared-feed snapshots while an active cached run has no job.
    if (!termScanJobState) {
      if ((previous && TERM_SCAN_RUNNING_STATES.has(previous)) || termScanRunInFlight) {
        void fetchLatestTermScan();
      }
      return;
    }
    if (previous !== termScanJobState && TERM_SCAN_TERMINAL_STATES.has(termScanJobState)) {
      void fetchLatestTermScan();
    }
  }, [activeJobs, fetchLatestTermScan, termScanJobState, termScanRunInFlight]);

  const markTermScanReviewed = useCallback(async () => {
    const userId = resolvedUserId || auth?.currentUser?.uid || '';
    if (!userId || !bookId || !termScanRun?.runId || termScanReviewed) {
      return;
    }
    try {
      const resp = await axios.post(
        `${apiBase}/books/${bookId}/term-scan/${termScanRun.runId}/reviewed`,
        { userId },
      );
      const reviewedAt =
        resp?.data?.run?.reviewedAt || resp?.data?.run?.reviewed_at || new Date().toISOString();
      setTermScanRun((prev: any) =>
        prev ? { ...prev, reviewedAt, reviewedBy: userId } : prev,
      );
    } catch {
      // ignore review tracking failures
    }
  }, [apiBase, auth?.currentUser?.uid, bookId, resolvedUserId, termScanReviewed, termScanRun?.runId]);

  useEffect(() => {
    if (isTermScanReviewOpen) {
      void markTermScanReviewed();
    }
  }, [isTermScanReviewOpen, markTermScanReviewed]);

  const handleTranMetadataEdit = (field: string) => {
    switch(field) {
      case 'title':
        setEditingTranMetadataTitle(true);
        setTempTranMetadataTitle(titleTranslation);
        break;
      case 'series':
        setEditingTranMetadataSeries(true);
        setTempTranMetadataSeries(seriesTranslation);
        break;
      case 'blurb':
        setEditingTranMetadataBlurb(true);
        setTempTranMetadataBlurb(blurbTranslation);
        break;
    }
  };

  const sanitizeInput = (input: string): string => {
    // Preserve all HTML to maintain formatting exactly as edited.
    // Do not strip or modify any tags so formatting carries over.
    return input;
  };

  const getTempMetadataValue = (field: string) => {
    switch (field) {
      case 'title':
        return tempTranMetadataTitle || '';
      case 'series':
        return tempTranMetadataSeries || '';
      case 'blurb':
        return tempTranMetadataBlurb || '';
      default:
        console.error('Unknown field:', field);
        return '';
    }
  };

  const getSavedMetadataValue = (field: string) => {
    switch (field) {
      case 'title':
        return titleTranslation || '';
      case 'series':
        return seriesTranslation || '';
      case 'blurb':
        return blurbTranslation || '';
      default:
        return '';
    }
  };

  const updateSavedMetadataValue = (field: string, value: string) => {
    switch(field) {
      case 'title':
        setTitleTranslations(prev => ({...prev, [pair]: value}));
        break;
      case 'series':
        setSeriesTranslations(prev => ({...prev, [pair]: value}));
        break;
      case 'blurb':
        setBlurbTranslations(prev => ({...prev, [pair]: value}));
        break;
    }
  };

  const saveTranMetadataField = async (field: string, pair: string, value: string) => {
    if (value === '') {
      return;
    }

    const { sourceLang, targetLang } = splitLanguagePair(pair);
    const sanitizedValue = sanitizeInput(value);

    const headers = await getAuthHeaders(auth.currentUser);
    const response = await axios.post(`${apiBase}/update-translation/`, {
      userId: auth.currentUser?.uid,
      bookId: bookId,
      field,
      value: sanitizedValue,
      sourceLang,
      targetLang,
      overwriteFlag: true,
    }, { headers });

    if (response.data.status !== 'success') {
      throw new Error(response.data.message);
    }

    updateSavedMetadataValue(field, sanitizedValue);
  };

  const getChangedManualMetadataFields = () => (
    ['title', ...(includeSeriesMetadata ? ['series'] : []), 'blurb'].filter((field) => {
      const value = getTempMetadataValue(field);
      return value !== '' && value !== getSavedMetadataValue(field);
    })
  );

  const changedManualMetadataFields = getChangedManualMetadataFields();
  const hasManualMetadataChanges = metadataFieldsMode === 'manual' && changedManualMetadataFields.length > 0;
  const manualBlurbOverLimit = metadataFieldsMode === 'manual'
    && changedManualMetadataFields.includes('blurb')
    && getBlurbWordCount(tempTranMetadataBlurb) > MAX_BLURB_TRANSLATION_WORDS;

  const handleTranMetadataSave = async (field: string, pair: string) => {
    const value = getTempMetadataValue(field);
    if (!value) {
      handleTranMetadataCancel(field);
      return;
    }
    if (field === 'blurb') {
      const blurbWordCount = getBlurbWordCount(value);
      if (blurbWordCount > MAX_BLURB_TRANSLATION_WORDS) {
        message.error(formatBlurbWordLimitMessage(blurbWordCount), 8);
        return;
      }
    }

    setIsLoading(true);
    try {
      await saveTranMetadataField(field, pair, value);
      handleTranMetadataCancel(field);
    } catch (error) {
      console.error(`Error saving ${field} translation:`, error);
      message.error(`Error Saving ${field} translation`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTranMetadataBatchSave = async () => {
    const changedFields = changedManualMetadataFields;
    if (changedFields.length === 0) {
      message.info('No localized metadata changes to save.');
      return;
    }
    if (changedFields.includes('blurb')) {
      const blurbWordCount = getBlurbWordCount(getTempMetadataValue('blurb'));
      if (blurbWordCount > MAX_BLURB_TRANSLATION_WORDS) {
        message.error(formatBlurbWordLimitMessage(blurbWordCount), 8);
        return;
      }
    }
    setIsLoading(true);
    try {
      for (const field of changedFields) {
        await saveTranMetadataField(field, pair, getTempMetadataValue(field));
      }
      message.success('Localized metadata saved.');
      onManualMetadataSaved?.();
    } catch (error) {
      console.error('Error saving localized metadata:', error);
      message.error('Error saving localized metadata');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTranMetadataBatchCancel = () => {
    setTempTranMetadataTitle(titleTranslation);
    setTempTranMetadataSeries(seriesTranslation);
    setTempTranMetadataBlurb(blurbTranslation);
    onManualMetadataSaved?.();
  };

  const handleTranMetadataCancel = (field: string) => {
    switch(field) {
      case 'title':
        setEditingTranMetadataTitle(false);
        break;
      case 'series':
        setEditingTranMetadataSeries(false);
        break;
      case 'blurb':
        setEditingTranMetadataBlurb(false);
        break;
    }
  };

  const renderEditableField = (
    field: string,
    value: string,
    editing: boolean,
    tempValue: string,
    setTempValue: (value: string) => void,
    options: { hideActions?: boolean; hideLabelText?: boolean; readOnly?: boolean } = {},
  ) => {
    const handleCopy = () => copyTranslatedMetadataValue(value);

    const modules = {
      toolbar: [
        ['bold', 'italic', 'underline'],
        ['clean'] // remove formatting button
      ]
    };

    const getLabel = (field: string) => {
      switch (field) {
        case 'title':
          return 'Translated Title:';
        case 'series':
          return 'Translated Series Name:';
        case 'seriesDescription':
          return 'Translated Series Description:';
        case 'blurb':
          return 'Translated Blurb:';
        default:
          return field;
      }
    };
    const normalizedValue = normalizeTranslatedMetadataValue(value);
    const hasTranslatedValue = Boolean(normalizedValue.trim());
    const handleBlurbChange = (nextValue: string, _delta?: unknown, source?: string) => {
      if (source && source !== 'user') {
        return;
      }
      if (!editing && !forceManualEditing) {
        handleTranMetadataEdit(field);
      }
      setTempValue(nextValue);
    };
    const fieldActionOffset = options.hideLabelText ? 0 : 10;

    return (
      <div style={{ display: 'flex', flexDirection: 'column'}}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          {!options.hideLabelText && <strong>{getLabel(field)}</strong>}
          {!options.hideActions && options.readOnly && (
            <div className="localized-metadata-shared-field-actions">
              {value && <CopyOutlined onClick={handleCopy} style={{ cursor: 'pointer' }} />}
              {onOpenSeriesManager && (
                <Button type="link" size="small" onClick={onOpenSeriesManager}>
                  Manage in Series
                </Button>
              )}
            </div>
          )}
          {!options.hideActions && !options.readOnly && !editing && (
            <div
              style={{ marginLeft: `${fieldActionOffset}px` }}
              data-tour-id={field === 'blurb' ? 'metadata-translation-actions' : undefined}
              data-testid={
                field === 'title'
                  ? 'tutorial:metadata:translated-title-actions'
                  : field === 'series'
                    ? 'tutorial:metadata:translated-series-actions'
                    : field === 'blurb'
                      ? 'tutorial:metadata:translated-blurb-inline-actions'
                      : undefined
              }
            >
              <EditOutlined
                onClick={() => handleTranMetadataEdit(field)}
                style={{ cursor: 'pointer', marginRight: '5px' }}
              />
              <CopyOutlined
                onClick={handleCopy}
                style={{ cursor: 'pointer', marginRight: field === 'blurb' ? '5px' : undefined }}
              />
              {field === 'blurb' && (
                <Tooltip title="Export Blurb to TXT">
                  <ExportOutlined
                    onClick={exportBlurbToTxt}
                    style={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              )}
            </div>
          )}
          {!options.hideActions && editing && !forceManualEditing && (
            <div style={{ marginLeft: `${fieldActionOffset}px` }}>
              <SaveOutlined
                onClick={() => handleTranMetadataSave(field, pair)}
                style={{ cursor: 'pointer', marginRight: '5px' }}
              />
              <CloseOutlined
                onClick={() => handleTranMetadataCancel(field)}
                style={{ cursor: 'pointer' }}
              />
            </div>
          )}
        </div>
        {editing ? (
          <>
            {field === 'blurb' ? (
              <>
                <ReactQuill
                  className="localized-metadata-blurb-editor"
                  value={sanitizeRichTextHtml(tempValue)}
                  onChange={handleBlurbChange}
                  modules={modules}
                  theme="snow"
                  placeholder="Enter translated blurb"
                />
                <BlurbWordCounter value={tempValue} />
              </>
            ) : field === 'seriesDescription' ? (
              <Input.TextArea
                className="localized-metadata-edit-input"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 10 }}
                placeholder="Enter translated series description"
              />
            ) : (
              <Input
                className="localized-metadata-edit-input"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
              />
            )}
          </>
        ) : (
          <>
            {field === 'blurb' ? (
              <>
                <ReactQuill
                  className="localized-metadata-blurb-editor localized-metadata-blurb-editor--store-listing"
                  value={sanitizeRichTextHtml(hasTranslatedValue ? normalizedValue : '')}
                  onChange={handleBlurbChange}
                  modules={modules}
                  theme="snow"
                  placeholder="Enter translated blurb"
                />
                <BlurbWordCounter value={hasTranslatedValue ? normalizedValue : ''} />
              </>
            ) : field === 'seriesDescription' ? (
              value ? (
                <div className="localized-metadata-display-box" style={{ whiteSpace: 'pre-wrap' }}>{value}</div>
              ) : (
                <div className="localized-metadata-display-box localized-metadata-display-box--placeholder">
                  <NotTranslatedPlaceholder />
                </div>
              )
            ) : (
              value ? (
                <div className="localized-metadata-display-box localized-metadata-display-box--single-line">{value}</div>
              ) : (
                <div className="localized-metadata-display-box localized-metadata-display-box--single-line localized-metadata-display-box--placeholder">
                  <NotTranslatedPlaceholder />
                </div>
              )
            )}
          </>
        )}
        {options.readOnly && (
          <div className="localized-metadata-shared-field-note">
            Shared across every linked book. Translate and edit this description in Series Management.
          </div>
        )}
      </div>
    );
  };

  const copyTranslatedMetadataValue = async (value: string) => {
      const safeValue = sanitizeRichTextHtml(value);
      try {
        // Create a temporary element to hold the HTML content
        const tempElement = document.createElement('div');
        tempElement.innerHTML = safeValue;

        // Get the plain text version
        const plainText = tempElement.textContent || tempElement.innerText;

        // Use the Clipboard API to set both HTML and plain text
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
            'text/html': new Blob([safeValue], { type: 'text/html' })
          })
        ]);

        message.success('Copied to clipboard');
      } catch (err) {
        console.error('Failed to copy: ', err);
        message.error('Failed to copy');
      }
    };

  const keywordElements = keywordsTranslation.map((keyword, index) => {
    const [keywordText, meaning] = keyword.split('|');
    return (
      <tr key={index}>
        <td><strong>{keywordText}</strong></td>
        <td>{meaning}</td>
      </tr>
    );
  });

  const translatedMetadataFields = [
    {
      key: 'title',
      value: titleTranslation,
      editing: editingTranMetadataTitle,
      tempValue: tempTranMetadataTitle,
      setTempValue: setTempTranMetadataTitle,
    },
    includeSeriesMetadata ? {
      key: 'series',
      value: seriesTranslation,
      editing: editingTranMetadataSeries,
      tempValue: tempTranMetadataSeries,
      setTempValue: setTempTranMetadataSeries,
    } : null,
    includeSeriesMetadata ? {
      key: 'seriesDescription',
      value: seriesDescriptionTranslation,
      editing: false,
      tempValue: seriesDescriptionTranslation,
      setTempValue: () => {},
      readOnly: true,
    } : null,
    {
      key: 'blurb',
      value: blurbTranslation,
      editing: editingTranMetadataBlurb,
      tempValue: tempTranMetadataBlurb,
      setTempValue: setTempTranMetadataBlurb,
    },
  ].filter(Boolean) as Array<{
    key: string;
    value: string;
    editing: boolean;
    tempValue: string;
    setTempValue: (value: string) => void;
    readOnly?: boolean;
  }>;
  const visibleTranslatedMetadataFields = translatedMetadataFields.filter((field) => {
    if (metadataFieldsMode === 'hidden') return false;
    if (metadataFieldsMode === 'manual' || metadataFieldsMode === 'all') return true;
    return hasTranslatedMetadataValue(field.value);
  });
  const forceManualEditing = metadataFieldsMode === 'manual';
  const shouldShowTranslatedMetadataCard = visibleTranslatedMetadataFields.length > 0;
  const shouldShowStorefrontTopGrid = metadataFieldsMode !== 'manual' && (shouldShowTranslatedMetadataCard || Boolean(storefrontCoverSection));
  const storefrontTopGridClassName = [
    'localized-metadata-top-grid',
    'book-detail-workbench-section',
    'book-detail-workbench-section--divided',
    !shouldShowTranslatedMetadataCard || !storefrontCoverSection ? 'localized-metadata-top-grid--single' : '',
  ].filter(Boolean).join(' ');
  const storeListingFields = visibleTranslatedMetadataFields.filter(field => field.key !== 'blurb');
  const translatedBlurbField = visibleTranslatedMetadataFields.find(field => field.key === 'blurb');
  const keywordPreviewCapacity = 10;
  const keywordPreviewLimit = keywordsTranslation.length > keywordPreviewCapacity
    ? keywordPreviewCapacity - 1
    : keywordPreviewCapacity;
  const keywordPreviewItems = keywordsTranslation.slice(0, keywordPreviewLimit);
  const hiddenKeywordCount = Math.max(0, keywordsTranslation.length - keywordPreviewLimit);
  const parseLocalizedKeyword = React.useCallback((keyword: string) => {
    const [keywordText, ...meaningParts] = String(keyword || '').split('|');
    return {
      keywordText: keywordText.trim(),
      meaning: meaningParts.join('|').trim(),
    };
  }, []);
  const copyLocalizedKeywords = React.useCallback(async (mode: 'keywords' | 'list') => {
    const lines = keywordsTranslation
      .map(parseLocalizedKeyword)
      .filter(item => item.keywordText)
      .map(item => {
        if (mode === 'keywords') return item.keywordText;
        return item.meaning ? `${item.keywordText} - ${item.meaning}` : item.keywordText;
      });
    if (!lines.length) {
      message.warning('No keywords to copy');
      return;
    }
    const text = mode === 'keywords' ? lines.join(', ') : lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      message.success(mode === 'keywords' ? 'Keywords copied' : 'Keyword list copied');
    } catch (error) {
      console.error('Failed to copy keywords', error);
      message.error('Failed to copy keywords');
    }
  }, [keywordsTranslation, parseLocalizedKeyword]);
  const localizedKeywordCopyActions = keywordsTranslation.length > 0 ? (
    <>
      <Tooltip title="Copy keyword phrases">
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyLocalizedKeywords('keywords')}
        >
          Copy keywords
        </Button>
      </Tooltip>
      <Tooltip title="Copy keyword phrases and meanings">
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyLocalizedKeywords('list')}
        >
          Copy list
        </Button>
      </Tooltip>
    </>
  ) : null;

  const renderTranslatedMetadataField = (
    field: (typeof visibleTranslatedMetadataFields)[number],
    className = 'localized-metadata-existing-field',
    editingOverride = forceManualEditing || field.editing,
    options: { hideActions?: boolean; hideLabelText?: boolean } = {},
  ) => (
    <div
      key={field.key}
      className={className}
      data-localized-metadata-field={field.key}
      tabIndex={-1}
    >
      {renderEditableField(
        field.key,
        field.value,
        editingOverride,
        field.tempValue,
        field.setTempValue,
        options,
      )}
    </div>
  );

  const renderStoreListingContent = () => (
    storeListingFields.length > 0 ? (
      <section
        className={`metadata-section localized-metadata-content-card localized-metadata-store-listing-card ${storeListingFields.length === 1 ? 'localized-metadata-content-card--single' : ''}`}
        data-tour-id="metadata-translation-section"
        data-testid="tutorial:metadata:translated-form"
      >
        <div className="metadata-section-heading book-detail-section-heading localized-metadata-content-heading">
          <div>
            <h3>Store listing</h3>
            <p>Localized title and series for this language edition.</p>
          </div>
        </div>
        <div className="localized-metadata-existing-fields localized-metadata-existing-fields--compact">
          {storeListingFields.map(field => renderTranslatedMetadataField(field))}
        </div>
      </section>
    ) : null
  );

  const renderTranslatedBlurbActions = () => {
    if (!translatedBlurbField) {
      return null;
    }

    const isEditing = forceManualEditing || translatedBlurbField.editing;

    if (isEditing && !forceManualEditing) {
      const translatedBlurbWordCount = getBlurbWordCount(tempTranMetadataBlurb);
      const translatedBlurbOverLimit = translatedBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS;
      return (
        <div className="metadata-blurb-tool-actions" data-tour-id="metadata-translation-actions" data-testid="tutorial:metadata:translated-blurb-actions">
          <Tooltip
            title={
              translatedBlurbOverLimit
                ? `Blurb must be ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer before saving.`
                : 'Save translated blurb'
            }
          >
            <Button
              type="primary"
              size="small"
              aria-label="Save translated blurb"
              onClick={() => handleTranMetadataSave('blurb', pair)}
              disabled={translatedBlurbOverLimit}
              icon={<SaveOutlined />}
            >
              Save blurb
            </Button>
          </Tooltip>
          <Tooltip title="Cancel editing">
            <Button
              size="small"
              aria-label="Cancel translated blurb edit"
              onClick={() => handleTranMetadataCancel('blurb')}
              icon={<CloseOutlined />}
            />
          </Tooltip>
        </div>
      );
    }

    if (isEditing) {
      return null;
    }

    return (
      <div className="metadata-blurb-tool-actions" data-tour-id="metadata-translation-actions" data-testid="tutorial:metadata:translated-blurb-actions">
        <Tooltip title="Edit translated blurb">
          <Button
            size="small"
            aria-label="Edit translated blurb"
            onClick={() => handleTranMetadataEdit('blurb')}
            icon={<EditOutlined />}
          >
            Edit blurb
          </Button>
        </Tooltip>
        <Tooltip title="Copy translated blurb">
          <Button
            size="small"
            aria-label="Copy translated blurb"
            onClick={() => copyTranslatedMetadataValue(translatedBlurbField.value)}
            icon={<CopyOutlined />}
          />
        </Tooltip>
        <Tooltip title="Export translated blurb to TXT">
          <Button
            size="small"
            aria-label="Export translated blurb to TXT"
            onClick={exportBlurbToTxt}
            icon={<ExportOutlined />}
          />
        </Tooltip>
      </div>
    );
  };

  const renderTranslatedBlurbContent = () => (
    translatedBlurbField ? (
      <section
        className="metadata-original-blurb-card localized-metadata-blurb-card"
        data-tour-id="metadata-translation-blurb-section"
      >
        <div className="metadata-blurb-field-header localized-metadata-blurb-field-header">
          <span className="metadata-blurb-label">Translated blurb</span>
          {renderTranslatedBlurbActions()}
        </div>
        <div className="localized-metadata-existing-fields localized-metadata-existing-fields--blurb">
          {renderTranslatedMetadataField(
            translatedBlurbField,
            'localized-metadata-existing-field localized-metadata-existing-field--wide',
            forceManualEditing || translatedBlurbField.editing,
            { hideActions: true, hideLabelText: true },
          )}
        </div>
      </section>
    ) : null
  );

  const renderGlossaryScanAction = (options?: { size?: MetadataActionButtonSize; className?: string }) => {
    const buttonSize = options?.size;
    const buttonSizeProps = buttonSize ? { size: buttonSize } : {};
    const showAction = termScanInFlight
      || showGlossaryScanReadyIndicator
      || canReviewGlossaryScan
      || !hideGlossaryScanAction;

    if (!showAction && !termScanLatestError) {
      return null;
    }

    return (
      <Space size={6} wrap className={options?.className}>
        {termScanInFlight ? (
          <Button
            {...buttonSizeProps}
            data-tour-id="metadata-glossary-scan"
            icon={<SearchOutlined />}
            disabled
          >
            Scan running
          </Button>
        ) : showGlossaryScanReadyIndicator ? (
          <Badge
            count="NEW"
            style={{
              backgroundColor: token.colorSuccess,
              color: token.colorTextLightSolid,
              fontSize: 10,
              boxShadow: `0 0 0 1px ${token.colorSuccessBorder}`,
            }}
          >
            <Button
              {...buttonSizeProps}
              className={AI_UTILITY_BUTTON_CLASS}
              icon={<EyeOutlined />}
              onClick={() => setIsTermScanReviewOpen(true)}
              disabled={reviewGlossaryScanDisabled || termScanLatestLoading}
              loading={termScanLatestLoading}
              data-testid="tutorial:term-scan:review-entry"
            >
              Review AI Glossary Scan
            </Button>
          </Badge>
        ) : canReviewGlossaryScan ? (
          <Button
            {...buttonSizeProps}
            className={AI_UTILITY_BUTTON_CLASS}
            icon={<EyeOutlined />}
            onClick={() => setIsTermScanReviewOpen(true)}
            disabled={reviewGlossaryScanDisabled || termScanLatestLoading}
            loading={termScanLatestLoading}
            data-testid="tutorial:term-scan:review-entry"
          >
            Review AI Glossary Scan
          </Button>
        ) : !hideGlossaryScanAction ? (
          <Button
            {...buttonSizeProps}
            className={AI_UTILITY_BUTTON_CLASS}
            data-tour-id="metadata-glossary-scan"
            data-testid="tutorial:term-scan:launch-entry"
            icon={<SearchOutlined />}
            onClick={handleOpenGlossaryScan}
          >
            Start AI Glossary Scan
          </Button>
        ) : null}
        {termScanLatestError && <Tag color="red">{termScanLatestError}</Tag>}
      </Space>
    );
  };

  const renderKeywordSuggestionAction = (options?: { className?: string }) => (
    onOpenKeywordSuggestions ? (
      <Tooltip title="Create localized keywords from your source metadata, or update the current list">
        <Button
          className={[AI_UTILITY_BUTTON_CLASS, options?.className].filter(Boolean).join(' ')}
          aria-label={`Suggest keywords for ${targetLang}`}
          data-tour-id={keywordsTranslation.length > 0 ? 'metadata-generate-keywords' : 'metadata-generate-keywords-empty'}
          data-testid="tutorial:metadata:keyword-suggestions"
          onClick={onOpenKeywordSuggestions}
          icon={<BulbOutlined />}
        >
          {keywordsTranslation.length > 0 ? 'Update localized keywords' : 'Create localized keywords'}
        </Button>
      </Tooltip>
    ) : null
  );

  const renderTranslationRulesContent = () => (
    <MetadataSection
      title="Translation rules"
      description="Set language behavior and protected terms before translating this edition."
      className="localized-guidance-section localized-guidance-section--compact book-detail-workbench-section"
      tourId="metadata-glossary-section"
      ariaBusy={termScanInFlight}
    >
      <div className={`localized-translation-rules-grid localized-translation-rules-grid--compact${translationGuidanceLead ? '' : ' localized-translation-rules-grid--glossary-only'}`}>
        <div className="localized-translation-rule-panel localized-translation-rule-panel--glossary" data-testid="tutorial:metadata:translation-rules-glossary">
          <div className="metadata-section-heading book-detail-section-heading localized-glossary-heading">
            <div>
              <h3>Glossary</h3>
              <p>Protected terms and fixed translations.</p>
            </div>
            {renderGlossaryScanAction({ className: 'localized-glossary-scan-action' })}
          </div>
          <div className="localized-glossary-rules localized-glossary-rules--compact">
            <div className="localized-glossary-rule">
              <div className="localized-glossary-rule-title">
                <strong>Never translate</strong>
                <Tooltip title="We will direct the AI to never translate these words or phrases.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </div>
              <div className="localized-glossary-rule-footer">
                <Space size="small" wrap>
                  <Tag>Book: {bookNeverTerms.length}</Tag>
                  <Tag color="red">Series: {seriesNeverTerms.length}</Tag>
                  <Tag>Account: {accountNeverTerms.length}</Tag>
                </Space>
	                <Button
	                  size="small"
	                  icon={<EditOutlined />}
	                  data-testid="tutorial:translation-rules:never"
	                  onClick={() => {
	                    setCurrentLanguagePair(pair);
	                    setIsNeverTranslateModalVisible(true);
	                  }}
	                >
	                  Manage never translate
	                </Button>
              </div>
            </div>
            <div className="localized-glossary-rule">
              <div className="localized-glossary-rule-title">
                <strong>Always translate</strong>
                <Tooltip title="We will direct the AI to always translate these source terms to their specified target equivalents.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </div>
              <div className="localized-glossary-rule-footer">
                <Space size="small" wrap>
                  <Tag>Book: {bookAlwaysPairs.length}</Tag>
                  <Tag color="blue">Series: {seriesAlwaysPairs.length}</Tag>
                  <Tag>Account: {accountAlwaysPairs.length}</Tag>
                </Space>
	                <Button
	                  size="small"
	                  icon={<EditOutlined />}
	                  data-testid="tutorial:translation-rules:always"
	                  onClick={() => {
	                    setCurrentLanguagePair(pair);
	                    setIsAlwaysTranslateModalVisible(true);
	                  }}
	                >
	                  Manage always translate
	                </Button>
              </div>
            </div>
          </div>
        </div>
        {translationGuidanceLead ? (
          <div className="localized-translation-rule-panel localized-translation-rule-panel--guidance" data-testid="tutorial:metadata:language-specific-guidance">
            {translationGuidanceLead}
          </div>
        ) : null}
      </div>
      {termScanInFlight && (
        <div className="localized-term-scan-overlay">
          <div role="status" aria-live="polite" className="localized-term-scan-status">
            <FeatherSpinner style={{ width: 72, height: 72 }} />
            <div className="localized-term-scan-status-copy">
              <div>AI Glossary Scan running...</div>
              <span>You can review the results once the scan completes.</span>
            </div>
          </div>
        </div>
      )}
    </MetadataSection>
  );

  const renderLocalizedKeywordsContent = () => (
    <MetadataSection
      title="Reader targeting & discovery"
      description="Create localized discovery terms from the source metadata, then copy or update them for this edition."
      className="localized-storefront-keywords-section localized-storefront-keywords-section--compact book-detail-workbench-section"
      tourId="metadata-keywords-section"
      actions={(
        <Space size={6} className="localized-keywords-actions">
          {localizedKeywordCopyActions}
          {renderKeywordSuggestionAction()}
        </Space>
      )}
    >
      <div data-testid="tutorial:metadata:translated-keywords-section">
        {keywordsTranslation.length > 0 ? (
          <div className="localized-keywords-preview-list">
            {keywordPreviewItems.map((keyword, index) => {
              const { keywordText, meaning } = parseLocalizedKeyword(keyword);
              return (
                <div className="localized-keyword-preview-row" key={`${keywordText}-${index}`}>
                  <strong>{keywordText}</strong>
                  {meaning ? <span>{meaning}</span> : null}
                </div>
              );
            })}
            {hiddenKeywordCount > 0 ? (
              <Tooltip title={keywordsTranslation.slice(keywordPreviewLimit).map(keyword => parseLocalizedKeyword(keyword).keywordText).join(', ')}>
                <span className="localized-keyword-preview-more">+{hiddenKeywordCount} more</span>
              </Tooltip>
            ) : null}
          </div>
        ) : (
          <div className="localized-keywords-empty-state localized-keywords-empty-state--compact">
            <div>
              <strong>
                <NotTranslatedPlaceholder className="localized-keywords-placeholder" />
              </strong>
              <p>Localized discovery terms are not translated yet.</p>
            </div>
          </div>
        )}
      </div>
    </MetadataSection>
  );

  const renderTermScanModals = () => (
    <>
      <TermScanLaunchModal
        open={isTermScanLaunchOpen}
        onClose={() => setIsTermScanLaunchOpen(false)}
        bookId={bookId}
        pair={pair}
        apiBase={apiBase}
        resolvedUserId={resolvedUserId}
        auth={auth}
        availableCredits={availableCredits}
        planId={creditBalance?.planID ?? null}
        fetchCreditBalance={fetchCreditBalance}
        onTopUpCredits={onTopUpCredits}
        onStarted={(run) => {
          setTermScanRun(run || null);
          setTermScanLatestError(null);
          const userId = resolvedUserId || auth?.currentUser?.uid || '';
          if (userId && run?.lockKey) {
            const queuedStatus: TranslationStatus = {
              lockGuid: run.lockKey,
              lockKey: run.lockKey,
              chapterGuid: 'term_scan',
              chapterName: 'AI Glossary Scan',
              sourceLang,
              targetLang,
              timestamp: new Date().toISOString(),
              status: 'queued',
              processingState: 'queued',
              cleared: false,
              bookId,
              userId,
              type: 'Glossary Scan',
              clientIntent: true,
            };
            addClientIntent(queuedStatus);
          }
          if (refreshTranslationsImmediate) {
            void refreshTranslationsImmediate(true);
          }
          message.success('AI glossary scan started.');
        }}
      />
      <Modal
        title={renderTermScanReviewModalTitle()}
        open={isTermScanReviewOpen}
        onCancel={() => setIsTermScanReviewOpen(false)}
        footer={null}
        width={termScanReviewModalWidth}
        destroyOnClose
        className={`term-scan-review-modal${isTermScanReviewExpanded ? ' term-scan-review-modal--expanded' : ''}`}
        style={{ top: isTermScanReviewExpanded ? 8 : 16 }}
        bodyStyle={termScanReviewModalBodyStyle}
        closable={!termScanApplying}
        maskClosable={!termScanApplying}
        keyboard={!termScanApplying}
      >
        <div style={termScanReviewModalContentStyle}>
          <TermScanPanel
            bookId={bookId}
            pair={pair}
            apiBase={apiBase}
            auth={auth}
            selectedSeriesId={selectedSeriesId}
            resolvedUserId={resolvedUserId}
            neverTranslateWords={neverTranslateWords}
            alwaysTranslateWords={alwaysTranslateWords}
            accountNeverTranslateWords={accountNeverTranslateWords}
            accountAlwaysTranslateWords={accountAlwaysTranslateWords}
            seriesNeverTranslateWords={seriesNeverTranslateWords}
            seriesAlwaysTranslateWords={seriesAlwaysTranslateWords}
            setNeverTranslateWords={setNeverTranslateWords}
            setAlwaysTranslateWords={setAlwaysTranslateWords}
            setAccountNeverTranslateWords={setAccountNeverTranslateWords}
            setAccountAlwaysTranslateWords={setAccountAlwaysTranslateWords}
            setSeriesTranslateRules={setSeriesTranslateRules}
            runInfo={termScanRun}
            onApplyingChange={setTermScanApplying}
            onTourControlChange={handleTermScanTourControlChange}
          />
          {termScanApplying && (
            <div className="localized-term-scan-modal-overlay">
              <div role="status" aria-live="polite" className="localized-term-scan-modal-status">
                <Spin size="large" />
                <div className="localized-term-scan-modal-copy">
                  <div>Applying suggestions...</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );

  if (resolvedContentMode === 'cover') {
    return <>{storefrontCoverSection}</>;
  }

  if (resolvedContentMode === 'storeListing') {
    return <>{renderStoreListingContent()}</>;
  }

  if (resolvedContentMode === 'blurb') {
    return <>{renderTranslatedBlurbContent()}</>;
  }

  if (resolvedContentMode === 'rules') {
    return (
      <>
        {renderTranslationRulesContent()}
        {renderTermScanModals()}
      </>
    );
  }

  if (resolvedContentMode === 'keywords') {
    return <>{renderLocalizedKeywordsContent()}</>;
  }

  return (
    <>
      <div className="localized-metadata-sections">
        {shouldShowStorefrontTopGrid && (
          <div className={storefrontTopGridClassName}>
            {storefrontCoverSection}
            {shouldShowTranslatedMetadataCard && (
              <section
                className={`metadata-section localized-metadata-content-card ${visibleTranslatedMetadataFields.length === 1 ? 'localized-metadata-content-card--single' : ''}`}
                data-tour-id="metadata-translation-section"
              >
                <div className="metadata-section-heading book-detail-section-heading localized-metadata-content-heading">
                  <div>
                    <h3>Store listing</h3>
                    <p>Localized title, series name, series description, and blurb for this language edition.</p>
                  </div>
                </div>
                <div className="localized-metadata-existing-fields">
                  {visibleTranslatedMetadataFields.map((field) => (
                    <div
                      key={field.key}
                      className={field.key === 'blurb' || field.key === 'seriesDescription' ? 'localized-metadata-existing-field localized-metadata-existing-field--wide' : 'localized-metadata-existing-field'}
                    >
                      {renderEditableField(
                        field.key,
                        field.value,
                        !field.readOnly && (forceManualEditing || field.editing),
                        field.tempValue,
                        field.setTempValue,
                        { readOnly: field.readOnly },
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
        {metadataFieldsMode === 'manual' && shouldShowTranslatedMetadataCard && (
          <section
            className={`metadata-section localized-metadata-content-card localized-metadata-content-card--manual ${visibleTranslatedMetadataFields.length === 1 ? 'localized-metadata-content-card--single' : ''}`}
            data-tour-id="metadata-translation-section"
          >
            <div className="metadata-section-heading book-detail-section-heading localized-metadata-content-heading">
              <div>
                <h3>Edit storefront metadata</h3>
                <p>Localized title, series name, series description, and blurb for this language edition.</p>
              </div>
              <Space wrap>
                <Button onClick={handleTranMetadataBatchCancel} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleTranMetadataBatchSave}
                  loading={isLoading}
                  disabled={!hasManualMetadataChanges || manualBlurbOverLimit}
                >
                  Save localized metadata
                </Button>
              </Space>
            </div>
            <div className="localized-metadata-existing-fields">
              {visibleTranslatedMetadataFields.map((field) => (
                <div
                  key={field.key}
                  className={field.key === 'blurb' || field.key === 'seriesDescription' ? 'localized-metadata-existing-field localized-metadata-existing-field--wide' : 'localized-metadata-existing-field'}
                >
                  {renderEditableField(
                    field.key,
                    field.value,
                    !field.readOnly,
                    field.tempValue,
                    field.setTempValue,
                    { readOnly: field.readOnly },
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        <MetadataSection
          title="Translation rules"
          description="Set language behavior and protected terms before translating this edition."
          className="localized-guidance-section localized-centered-section-heading book-detail-workbench-section book-detail-workbench-section--divided"
          tourId="metadata-glossary-section"
          ariaBusy={termScanInFlight}
        >
          <div className={`localized-translation-rules-grid${translationGuidanceLead ? '' : ' localized-translation-rules-grid--glossary-only'}`}>
            <div className="localized-translation-rule-panel localized-translation-rule-panel--glossary">
              <div className="metadata-section-heading book-detail-section-heading localized-glossary-heading">
                <div>
                  <h3>Glossary</h3>
                  <p>Protected terms for names, phrases, brands, and fixed translations.</p>
                </div>
                <Space wrap>
                  {termScanInFlight ? (
                    <Button
                      data-tour-id="metadata-glossary-scan"
                      icon={<SearchOutlined />}
                      disabled
                    >
                      Scan running
                    </Button>
                  ) : showGlossaryScanReadyIndicator ? (
                    <Badge
                      count="NEW"
                      style={{
                        backgroundColor: token.colorSuccess,
                        color: token.colorTextLightSolid,
                        fontSize: 10,
                        boxShadow: `0 0 0 1px ${token.colorSuccessBorder}`,
                      }}
                    >
                      <Button
                        className={AI_UTILITY_BUTTON_CLASS}
                        icon={<EyeOutlined />}
                        onClick={() => setIsTermScanReviewOpen(true)}
                        disabled={reviewGlossaryScanDisabled || termScanLatestLoading}
                        loading={termScanLatestLoading}
                        data-testid="tutorial:term-scan:review-entry"
                      >
                        Review AI Glossary Scan
                      </Button>
                    </Badge>
                  ) : canReviewGlossaryScan ? (
                    <Button
                      className={AI_UTILITY_BUTTON_CLASS}
                      icon={<EyeOutlined />}
                      onClick={() => setIsTermScanReviewOpen(true)}
                      disabled={reviewGlossaryScanDisabled || termScanLatestLoading}
                      loading={termScanLatestLoading}
                      data-testid="tutorial:term-scan:review-entry"
                    >
                      Review AI Glossary Scan
                    </Button>
                  ) : !hideGlossaryScanAction ? (
                    <Button
                      className={AI_UTILITY_BUTTON_CLASS}
                      data-tour-id="metadata-glossary-scan"
                      data-testid="tutorial:term-scan:launch-entry"
                      icon={<SearchOutlined />}
                      onClick={handleOpenGlossaryScan}
                    >
                      Start AI Glossary Scan
                    </Button>
                  ) : null}
                  {termScanLatestError && <Tag color="red">{termScanLatestError}</Tag>}
                </Space>
              </div>
              <div className="localized-glossary-rules">
                <div className="localized-glossary-rule">
                  <div className="localized-glossary-rule-title">
                    <strong>Never translate</strong>
                    <Tooltip title="We will direct the AI to never translate these words or phrases.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </div>
                  <div className="localized-glossary-rule-footer">
                    <Space size="small" wrap>
                      <Tag>Book: {bookNeverTerms.length}</Tag>
                      <Tag color="red">Series: {seriesNeverTerms.length}</Tag>
                      <Tag>Account: {accountNeverTerms.length}</Tag>
                    </Space>
	                    <Button
	                      size="small"
	                      icon={<EditOutlined />}
	                      data-testid="tutorial:translation-rules:never"
	                      onClick={() => {
	                        setCurrentLanguagePair(pair);
	                        setIsNeverTranslateModalVisible(true);
	                      }}
	                    >
	                      Manage never translate
	                    </Button>
                  </div>
                </div>
                <div className="localized-glossary-rule">
                  <div className="localized-glossary-rule-title">
                    <strong>Always translate</strong>
                    <Tooltip title="We will direct the AI to always translate these source terms to their specified target equivalents.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </div>
                  <div className="localized-glossary-rule-footer">
                    <Space size="small" wrap>
                      <Tag>Book: {bookAlwaysPairs.length}</Tag>
                      <Tag color="blue">Series: {seriesAlwaysPairs.length}</Tag>
                      <Tag>Account: {accountAlwaysPairs.length}</Tag>
                    </Space>
	                    <Button
	                      size="small"
	                      icon={<EditOutlined />}
	                      data-testid="tutorial:translation-rules:always"
	                      onClick={() => {
	                        setCurrentLanguagePair(pair);
	                        setIsAlwaysTranslateModalVisible(true);
	                      }}
	                    >
	                      Manage always translate
	                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {translationGuidanceLead ? (
              <div className="localized-translation-rule-panel localized-translation-rule-panel--guidance">
                {translationGuidanceLead}
              </div>
            ) : null}
          </div>
            {termScanInFlight && (
              <div className="localized-term-scan-overlay">
                <div
                  role="status"
                  aria-live="polite"
                  className="localized-term-scan-status"
                >
                  <FeatherSpinner style={{ width: 72, height: 72 }} />
                  <div className="localized-term-scan-status-copy">
                    <div>AI Glossary Scan running…</div>
                    <span>You can review the results once the scan completes.</span>
                  </div>
                </div>
              </div>
            )}
        </MetadataSection>

        <MetadataSection
          title="Reader targeting & discovery"
          description="Create localized discovery terms from the source metadata, then copy or update them for this edition."
          className="localized-storefront-keywords-section localized-centered-section-heading book-detail-workbench-section book-detail-workbench-section--divided"
          tourId="metadata-keywords-section"
          actions={(
            <Space size={6} className="localized-keywords-actions">
              {localizedKeywordCopyActions}
              {renderKeywordSuggestionAction()}
            </Space>
          )}
        >
          <div data-testid="tutorial:metadata:translated-keywords-section">
          {keywordsTranslation.length > 0 ? (
            <table className="localized-keywords-table">
              <thead>
                <tr>
                  <th>Keyword in {targetLang}</th>
                  <th>Meaning in {sourceLang}</th>
                </tr>
              </thead>
              <tbody>
                {keywordElements}
              </tbody>
            </table>
          ) : (
            <div className="localized-keywords-empty-state">
              <div>
                <strong>
                  <NotTranslatedPlaceholder className="localized-keywords-placeholder" />
                </strong>
                <p>Localized discovery terms for this storefront are not translated yet.</p>
              </div>
            </div>
          )}
          </div>
        </MetadataSection>
      </div>
      <TermScanLaunchModal
        open={isTermScanLaunchOpen}
        onClose={() => setIsTermScanLaunchOpen(false)}
        bookId={bookId}
        pair={pair}
        apiBase={apiBase}
        resolvedUserId={resolvedUserId}
        auth={auth}
        availableCredits={availableCredits}
        planId={creditBalance?.planID ?? null}
        fetchCreditBalance={fetchCreditBalance}
        onTopUpCredits={onTopUpCredits}
        onStarted={(run) => {
          setTermScanRun(run || null);
          setTermScanLatestError(null);
          const userId = resolvedUserId || auth?.currentUser?.uid || '';
          if (userId && run?.lockKey) {
            const queuedStatus: TranslationStatus = {
              lockGuid: run.lockKey,
              lockKey: run.lockKey,
              chapterGuid: 'term_scan',
              chapterName: 'AI Glossary Scan',
              sourceLang,
              targetLang,
              timestamp: new Date().toISOString(),
              status: 'queued',
              processingState: 'queued',
              cleared: false,
              bookId,
              userId,
              type: 'Glossary Scan',
              clientIntent: true,
            };
            addClientIntent(queuedStatus);
          }
          if (refreshTranslationsImmediate) {
            void refreshTranslationsImmediate(true);
          }
          message.success('AI glossary scan started.');
        }}
      />
      <Modal
        title={renderTermScanReviewModalTitle()}
        open={isTermScanReviewOpen}
        onCancel={() => setIsTermScanReviewOpen(false)}
        footer={null}
        width={termScanReviewModalWidth}
        destroyOnClose
        className={`term-scan-review-modal${isTermScanReviewExpanded ? ' term-scan-review-modal--expanded' : ''}`}
        style={{ top: isTermScanReviewExpanded ? 8 : 16 }}
        bodyStyle={termScanReviewModalBodyStyle}
        closable={!termScanApplying}
        maskClosable={!termScanApplying}
        keyboard={!termScanApplying}
      >
        <div style={termScanReviewModalContentStyle}>
          <TermScanPanel
            bookId={bookId}
            pair={pair}
            apiBase={apiBase}
            auth={auth}
            selectedSeriesId={selectedSeriesId}
            resolvedUserId={resolvedUserId}
            neverTranslateWords={neverTranslateWords}
            alwaysTranslateWords={alwaysTranslateWords}
            accountNeverTranslateWords={accountNeverTranslateWords}
            accountAlwaysTranslateWords={accountAlwaysTranslateWords}
            seriesNeverTranslateWords={seriesNeverTranslateWords}
            seriesAlwaysTranslateWords={seriesAlwaysTranslateWords}
            setNeverTranslateWords={setNeverTranslateWords}
            setAlwaysTranslateWords={setAlwaysTranslateWords}
            setAccountNeverTranslateWords={setAccountNeverTranslateWords}
            setAccountAlwaysTranslateWords={setAccountAlwaysTranslateWords}
            setSeriesTranslateRules={setSeriesTranslateRules}
            runInfo={termScanRun}
            onApplyingChange={setTermScanApplying}
            onTourControlChange={handleTermScanTourControlChange}
          />
          {termScanApplying && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: token.colorBgMask,
                zIndex: 2,
              }}
            >
              <div
                role="status"
                aria-live="polite"
                style={{
                  background: token.colorBgElevated,
                  borderRadius: 12,
                  padding: 24,
                  boxShadow: token.boxShadowSecondary,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 280,
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <Spin size="large" />
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>Applying suggestions…</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

    </>
  );
};

export default MetadataTranslationTabContent;
